/**
 * Estado global de arranque: qué sabe la app antes de poder mostrar nada.
 *
 * El orden de las comprobaciones importa y es el que decide a dónde va el
 * usuario:
 *
 *   ¿hay conexión configurada?  no  → /setup
 *   ¿conecta?                   no  → /setup (con el error)
 *   ¿esquema migrado?           no  → /setup (paso migrar)
 *   ¿existe algún usuario?      no  → /setup (crear admin)
 *   ¿hay sesión?                no  → /login
 *   sí                              → la aplicación
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { getStatus, logout, type SessionUser, type SystemStatus } from '@/lib/api'

interface SessionValue {
  status: SystemStatus | null
  user: SessionUser | null
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
  signOut: () => Promise<void>
}

const SessionContext = createContext<SessionValue | null>(null)

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SystemStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      setStatus(await getStatus())
      setError(null)
    } catch (err) {
      setStatus(null)
      setError(err instanceof Error ? err.message : 'Error desconocido')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const signOut = useCallback(async () => {
    await logout().catch(() => {})
    await refresh()
  }, [refresh])

  const value = useMemo<SessionValue>(
    () => ({ status, user: status?.user ?? null, loading, error, refresh, signOut }),
    [status, loading, error, refresh, signOut],
  )

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession(): SessionValue {
  const ctx = useContext(SessionContext)
  if (!ctx) throw new Error('useSession debe usarse dentro de <SessionProvider>')
  return ctx
}

export type BootStage = 'loading' | 'needs-setup' | 'needs-login' | 'ready'

export function bootStage(status: SystemStatus | null, loading: boolean): BootStage {
  if (loading || !status) return 'loading'
  if (!status.configured || !status.connected || !status.schemaReady) return 'needs-setup'
  if (status.firstRun) return 'needs-setup'
  if (!status.user) return 'needs-login'
  return 'ready'
}
